import { Injectable } from "@angular/core";
import { UserService } from "@main-services/user.service";
import { Fiat } from "@models";
import { combineLatest, Observable } from "rxjs";
import { take } from "rxjs/operators";
import { StakingOverviewState } from "../entities/staking-overview-state.model";
import { StakingOverviewStateService } from "./staking-overview.state.service";
import { StakingHelperService } from "./utility/staking-helper.service";
import { StakingOverviewRemote } from "@modules/staking/domain/infrastructure/staking-overview.remote";
import { FeatureAvailabilityService } from "@core-services/feature-availability.service";
import { Feature } from "@models/feature-list";
import { StakingFacade } from "@modules/staking/domain/+state/staking.facade";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { PortfolioFacade } from "@modules/portfolio/domain/portfolio.facade";
import { PortfolioAssetDetails } from "@modules/portfolio/domain/entities/portfolio-overview.model";

@UntilDestroy()
@Injectable()
export class StakingOverviewFacade {
	constructor(
		private readonly stakingOverviewStateService: StakingOverviewStateService,
		private readonly stakingHelperService: StakingHelperService,
		private readonly stakingRemote: StakingOverviewRemote,
		private readonly stakingFacade: StakingFacade,
		private readonly userService: UserService,
		private readonly featureAvailability: FeatureAvailabilityService,
		private readonly portfolioFacade: PortfolioFacade
	) {
		if (this.userService.user.isVerified()) {
			combineLatest({
				stakeables: this.stakingFacade.getStakeables$(),
				portfolioState: this.portfolioFacade.getPortfolioOverviewState$()
			})
				.pipe(untilDestroyed(this))
				.subscribe(({ portfolioState }) => {
					const ownedCryptos = portfolioState?.portfolios?.filter(
						(portfolioItem) => portfolioItem?.asset_group === "COIN"
					);
					this.fetchStakingOverviewInfo(ownedCryptos ?? []);
				});
		}
	}

	public fetchStakingOverviewInfo(ownedAssets?: PortfolioAssetDetails[]): void {
		this.stakingRemote
			.fetchOverviewInfo(ownedAssets)
			.pipe(take(1))
			.subscribe(
				(state: StakingOverviewState) => this.stakingOverviewStateService.updateOverviewState(state),
				() => this.stakingOverviewStateService.updateOverviewState({ hasError: true })
			);
	}

	public getState$(): Observable<StakingOverviewState> {
		return this.stakingOverviewStateService.getOverviewState$();
	}

	public stakingEnabled$(): Observable<boolean> {
		return this.featureAvailability.getAvailability(Feature.STAKING_GLOBAL);
	}

	public getDefaultFiat(): Fiat {
		return this.stakingHelperService.getFiat();
	}
}
